
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = supabaseUrl && supabaseKey ? createClient(supabaseUrl, supabaseKey) : null;

interface Product {
  product_id: string;
  product_name: string;
  product_image_url: string;
  brand_name: string | null;
  carton_price: number;
  pair_price: number;
  pairs_per_carton: number;
  status: string;
  is_featured: boolean;
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_featured', true)
        .eq('status', 'active')
        .order('updated_at', { ascending: false })
        .limit(8);

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading featured products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductClick = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  if (isLoading) {
    return (
      <section className="w-full bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Featured Products
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Handpicked wholesale products recommended for your business
          </p>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600">No featured products available.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product.product_id}
                onClick={() => handleProductClick(product.product_id)}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
              >
                {/* Product Image */}
                <div className="relative w-full h-64 bg-gray-100">
                  <img
                    src={product.product_image_url}
                    alt={product.product_name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>

                {/* Product Info */}
                <div className="p-4">
                  {/* Brand Name */}
                  {product.brand_name && (
                    <p className="text-sm text-gray-500 mb-1">{product.brand_name}</p>
                  )}

                  {/* Product Name */}
                  <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3rem]">
                    {product.product_name}
                  </h3>

                  {/* Pricing */}
                  <div className="mb-2">
                    <p className="text-lg font-bold text-blue-600">
                      ${product.carton_price.toFixed(2)}/carton
                    </p>
                    <p className="text-sm text-gray-500">
                      ${product.pair_price.toFixed(2)}/pair
                    </p>
                  </div>

                  {/* Pairs per Carton */}
                  <p className="text-sm text-gray-600">
                    {product.pairs_per_carton} pairs/carton
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
